import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Phone, CheckCircle2 } from 'lucide-react';
export function MortgageCalculator() {
  const [homePrice, setHomePrice] = useState(500000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(6.75);
  const [loanTerm, setLoanTerm] = useState(30);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [principalAndInterest, setPrincipalAndInterest] = useState(0);
  const [propertyTax, setPropertyTax] = useState(0);
  const [homeInsurance, setHomeInsurance] = useState(150);
  // Lead capture state
  const [formPhase, setFormPhase] = useState<'cta' | 'form' | 'success'>('cta');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Lead captured:', {
      name,
      email,
      phone,
      calculatorState: {
        homePrice,
        downPaymentPercent,
        interestRate,
        loanTerm,
        monthlyPayment
      }
    });
    setFormPhase('success');
  };
  useEffect(() => {
    const principal = homePrice - homePrice * (downPaymentPercent / 100);
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    let pi = 0;
    if (monthlyRate === 0) {
      pi = principal / numberOfPayments;
    } else {
      pi = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    }
    const tax = homePrice * 0.0053 / 12; // 0.53% Nevada rate
    const insurance = 150; // Estimated monthly
    setPrincipalAndInterest(pi);
    setPropertyTax(tax);
    setHomeInsurance(insurance);
    setMonthlyPayment(pi + tax + insurance);
  }, [homePrice, downPaymentPercent, interestRate, loanTerm]);
  const chartData = [{
    name: 'Principal & Interest',
    value: principalAndInterest,
    color: '#C9A96E'
  }, {
    name: 'Property Tax',
    value: propertyTax,
    color: '#1A1A1A'
  }, {
    name: 'Home Insurance',
    value: homeInsurance,
    color: '#888888'
  }];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <section className="py-24 bg-warmGray px-6" data-id="element-318">
      <div className="max-w-6xl mx-auto" data-id="element-319">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-320">
          <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-321">
            Financial Planning
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-charcoal" data-id="element-322">
            Mortgage <span className="italic text-gold" data-id="element-323">Calculator</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start bg-white p-8 md:p-12 shadow-sm border border-warmGray-dark" data-id="element-324">
          {/* Left Side: Inputs */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-8" data-id="element-325">
            {/* Home Price */}
            <div data-id="element-326">
              <div className="flex justify-between mb-2" data-id="element-327">
                <label className="text-sm uppercase tracking-wider text-charcoal-light font-medium" data-id="element-328">
                  Home Price
                </label>
                <span className="font-serif text-xl text-charcoal" data-id="element-329">
                  {formatCurrency(homePrice)}
                </span>
              </div>
              <input type="range" min="200000" max="5000000" step="10000" value={homePrice} onChange={e => setHomePrice(Number(e.target.value))} className="w-full h-1 bg-warmGray-dark rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-330" />
            </div>

            {/* Down Payment */}
            <div data-id="element-331">
              <div className="flex justify-between mb-2" data-id="element-332">
                <label className="text-sm uppercase tracking-wider text-charcoal-light font-medium" data-id="element-333">
                  Down Payment ({downPaymentPercent}%)
                </label>
                <span className="font-serif text-xl text-charcoal" data-id="element-334">
                  {formatCurrency(homePrice * (downPaymentPercent / 100))}
                </span>
              </div>
              <input type="range" min="5" max="50" step="1" value={downPaymentPercent} onChange={e => setDownPaymentPercent(Number(e.target.value))} className="w-full h-1 bg-warmGray-dark rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-335" />
            </div>

            {/* Interest Rate */}
            <div data-id="element-336">
              <div className="flex justify-between mb-2" data-id="element-337">
                <label className="text-sm uppercase tracking-wider text-charcoal-light font-medium" data-id="element-338">
                  Interest Rate (%)
                </label>
                <span className="font-serif text-xl text-charcoal" data-id="element-339">
                  {interestRate}%
                </span>
              </div>
              <input type="number" min="1" max="15" step="0.125" value={interestRate} onChange={e => setInterestRate(Number(e.target.value))} className="w-full px-4 py-3 border border-warmGray-dark focus:outline-none focus:border-gold transition-colors bg-warmGray" data-id="element-340" />
            </div>

            {/* Loan Term */}
            <div data-id="element-341">
              <label className="text-sm uppercase tracking-wider text-charcoal-light font-medium block mb-3" data-id="element-342">
                Loan Term
              </label>
              <div className="flex gap-4" data-id="element-343">
                <button onClick={() => setLoanTerm(15)} className={`flex-1 py-3 border transition-colors ${loanTerm === 15 ? 'bg-charcoal text-white border-charcoal' : 'bg-transparent text-charcoal border-warmGray-dark hover:border-gold'}`} data-id="element-344">
                  15 Years
                </button>
                <button onClick={() => setLoanTerm(30)} className={`flex-1 py-3 border transition-colors ${loanTerm === 30 ? 'bg-charcoal text-white border-charcoal' : 'bg-transparent text-charcoal border-warmGray-dark hover:border-gold'}`} data-id="element-345">
                  30 Years
                </button>
              </div>
            </div>
          </motion.div>

          {/* Right Side: Results */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-col items-center justify-center h-full" data-id="element-346">
            <div className="text-center mb-8" data-id="element-347">
              <span className="text-sm uppercase tracking-wider text-charcoal-light block mb-2" data-id="element-348">
                Estimated Monthly Payment
              </span>
              <div className="text-5xl md:text-6xl font-serif text-charcoal" data-id="element-349">
                {formatCurrency(monthlyPayment)}
              </div>
            </div>

            <div className="w-full h-64 mb-8" data-id="element-350">
              <ResponsiveContainer width="100%" height="100%" data-id="element-351">
                <PieChart data-id="element-352">
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value" data-id="element-353">
                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-354" />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                  borderRadius: '0px',
                  border: '1px solid #EAE8E3'
                }} data-id="element-355" />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full space-y-3 mb-10" data-id="element-356">
              {chartData.map((item, index) => <div key={index} className="flex justify-between items-center text-sm" data-id="element-357">
                  <div className="flex items-center gap-2" data-id="element-358">
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: item.color
                }} data-id="element-359"></div>
                    <span className="text-charcoal-light" data-id="element-360">{item.name}</span>
                  </div>
                  <span className="font-medium text-charcoal" data-id="element-361">
                    {formatCurrency(item.value)}
                  </span>
                </div>)}
            </div>

            <div className="w-full border-t border-warmGray-dark pt-8 relative min-h-[200px]" data-id="element-362">
              <AnimatePresence mode="wait" data-id="element-363">
                {formPhase === 'cta' && <motion.div key="cta" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="text-center" data-id="element-364">
                    <h4 className="font-serif text-xl text-charcoal mb-2" data-id="element-365">
                      Get Your Personalized Rate Quote
                    </h4>
                    <p className="text-sm text-charcoal-light mb-6" data-id="element-366">
                      Jenny works with trusted local lenders who can beat these
                      estimates. Get a detailed breakdown and pre-approval
                      guidance sent to your inbox.
                    </p>
                    <button onClick={() => setFormPhase('form')} className="inline-block bg-gold text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-dark transition-colors duration-300 w-full sm:w-auto" data-id="element-367">
                      Get My Free Quote
                    </button>
                  </motion.div>}

                {formPhase === 'form' && <motion.form key="form" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} onSubmit={handleLeadSubmit} className="text-left w-full" data-id="element-368">
                    <h4 className="font-serif text-xl text-charcoal mb-4 text-center" data-id="element-369">
                      Where should we send your quote?
                    </h4>
                    <div className="space-y-4 mb-6" data-id="element-370">
                      <div data-id="element-371">
                        <input type="text" required placeholder="Name" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 border border-warmGray-dark focus:outline-none focus:border-gold transition-colors bg-warmGray" data-id="element-372" />
                      </div>
                      <div data-id="element-373">
                        <input type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-warmGray-dark focus:outline-none focus:border-gold transition-colors bg-warmGray" data-id="element-374" />
                      </div>
                      <div data-id="element-375">
                        <input type="tel" placeholder="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-3 border border-warmGray-dark focus:outline-none focus:border-gold transition-colors bg-warmGray" data-id="element-376" />
                      </div>
                    </div>
                    <button type="submit" className="w-full bg-gold text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-dark transition-colors duration-300 mb-3" data-id="element-377">
                      Send My Quote
                    </button>
                    <p className="text-xs text-charcoal-light text-center" data-id="element-378">
                      Your information is private and will only be used to
                      connect you with Jenny.
                    </p>
                  </motion.form>}

                {formPhase === 'success' && <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.4
              }} className="text-center" data-id="element-379">
                    <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-4" data-id="element-380" />
                    <h4 className="font-serif text-2xl text-charcoal mb-2" data-id="element-381">
                      Thank you, {name || 'there'}!
                    </h4>
                    <p className="text-sm text-charcoal-light mb-6" data-id="element-382">
                      Jenny will review your numbers and reach out with
                      personalized rate options within 24 hours.
                    </p>
                    <a href="tel:+18583978218" className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-6 py-3 uppercase tracking-widest text-xs hover:bg-charcoal hover:text-white transition-colors duration-300" data-id="element-383">
                      <Phone className="w-3 h-3" data-id="element-384" />
                      Can't wait? Call (858) 397-8218
                    </a>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}